import {
  FluentProvider,
  Spinner,
  tokens,
  Toaster,
  useToastController,
} from "@fluentui/react-components";
import { Navigate, Route, Routes  } from "react-router-dom";
import Timesheet from "./Timesheet";
import {useGlobalStatus, GlobalStatusProvider} from '../../services/GlobalStatusProvider';
import { useTeams } from '@microsoft/teamsfx-react';
import {useState, useEffect}  from "react";
import {GetA23WeekTimeEntries, GetTimesheetData} from '../../services/apiService';
import {AddNewTimecode, setTimesheetEntryStatus} from '../../services/TimesheetDataService';
import {getDay, previousMonday, startOfDay} from "date-fns";
import { showErrorToast, TOASTER_ID } from "../../services/toastUtils";

export default function TimesheetApp() {
  const [timesheetData, setTimesheetData]= useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [isTimesheetSubmitted, setIsTimesheetSubmitted] = useState(false);
  const [isAddTimecodeDisabled, setIsAddTimecodeDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timesheetStatusMessage, setTimesheetStatusMessage] = useState("");
  const [timesheetStatus, setTimesheetStatus] = useState(false);
  const { dispatchToast } = useToastController(TOASTER_ID);
  const [isCasualSubmitLocked, setIsCasualSubmitLocked] = useState(false);

  const getEntries = async () => {
    setLoading(true);

    var today = Date();
    const currentWeekStart = getDay(today) > 1 ? previousMonday(startOfDay(today)) : startOfDay(today);
    let existingResources = [];
    try {
      existingResources = await GetA23WeekTimeEntries(currentWeekStart);
    } catch {
      showErrorToast(dispatchToast, "Failed to get time entries");
    }
    
    let updatedTimesheetData = [];
    let casualSubmitLocked = false;
    existingResources.forEach(resource => {
        if (resource.timeEntries.length > 0) {
          resource.timeEntries.forEach(entry => {
              if (entry.casualMonthLocked && new Date(entry.date) <= currentWeekStart) casualSubmitLocked = true;
            });
            // Add the resource info if it has existing entries for the week
            updatedTimesheetData = AddNewTimecode(
                currentWeekStart,
                updatedTimesheetData,
                {clientId :resource.clientId, clientName: resource.clientName},
                {projectId: resource.projectId, projectName: resource.projectName, projectStartDate: resource.projectStart, projectEndDate: resource.projectEnd},
                {budgetId: resource.budgetId, budgetName: resource.budgetName},
                {taskId: resource.taskId, taskName: resource.taskName, taskStartDate: resource.taskStart, taskEndDate: resource.taskEnd},
                {userStartDate: resource.userMapping.userStart, userEndDate: resource.userMapping.userEnd},
                resource
            );
        }
    });
    // Need to do it this way otherwise it will stay set to true when cycling weeks if it has been set
    if (casualSubmitLocked) {
        setIsCasualSubmitLocked(true);
    } else {
        setIsCasualSubmitLocked(false);
    }

    // Get timesheet status
    let timesheet = '';
    try {
      timesheet = await GetTimesheetData(currentWeekStart);
    } catch {
      showErrorToast(dispatchToast, "Failed to get timesheet status");
    }
    
    if (timesheet.timesheetStatus === "Submitted") {
        updatedTimesheetData.forEach(client => {
            client.projects.forEach(project => {
                project.budgets.forEach(budget => {
                    budget.timecodes.forEach(timecode => {
                        timecode.entryDays.forEach(day => {
                            day.status = "closed";
                        });
                    });
                });
            });
        });
        setIsAddTimecodeDisabled(true);
        setIsTimesheetSubmitted(true);
    }

    setLeaveData(timesheet.leaveDays);

    switch (timesheet.timesheetStatus) {
      case "Submitted":
        setTimesheetEntryStatus(updatedTimesheetData, "closed");
        setTimesheetStatusMessage("Timesheet Submitted");
        setIsAddTimecodeDisabled(true);
        setIsTimesheetSubmitted(true);
        setTimesheetStatus(false);
        break;
      case "Needs Attention":
        setTimesheetEntryStatus(updatedTimesheetData, "rejected");
        setTimesheetStatusMessage("Timesheet Rejected!");
        setTimesheetStatus(true);
        break;
      case "Approved":
        setTimesheetEntryStatus(updatedTimesheetData, "approved");
        setTimesheetStatusMessage("Timesheet Approved :)");
        setIsAddTimecodeDisabled(true);
        setIsTimesheetSubmitted(true);
        setTimesheetStatus(false);
        break;
      default:
        setTimesheetStatusMessage("");
        setTimesheetStatus(true);
      }

    setTimesheetData(updatedTimesheetData);
    setLoading(false);
  }

  // App initialisation
  useEffect(() => {
    getEntries();
  }, []);

  
  return (
    <GlobalStatusProvider loading={loading} setLoading={setLoading} isAddTimecodeDisabled={isAddTimecodeDisabled} setIsAddTimecodeDisabled={setIsAddTimecodeDisabled} 
    isTimesheetSubmitted={isTimesheetSubmitted} setIsTimesheetSubmitted={setIsTimesheetSubmitted} timesheetStatus={timesheetStatus} setTimesheetStatus={setTimesheetStatus}
    isCasualSubmitLocked={isCasualSubmitLocked} setIsCasualSubmitLocked={setIsCasualSubmitLocked}>
      <Toaster toasterId={TOASTER_ID} />
      <AppContent timesheetData={timesheetData} leaveData={leaveData} setLeaveData={setLeaveData} setTimesheetData={setTimesheetData} timesheetStatusMessage={timesheetStatusMessage} setTimesheetStatusMessage={setTimesheetStatusMessage} />
    </GlobalStatusProvider>
  );
}

function AppContent(props) {
  const [{theme}] = useTeams({});
  const{loading}= useGlobalStatus();

  return (
    <FluentProvider
    theme={theme}
    style={{ background: tokens.colorNeutralBackground3 }}>     
    <div style={{padding:20}}>
      {loading ? (
        <div style={{position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: '100vw',
          backgroundColor: theme.colorNeutralBackground1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.85,
          zIndex: 9999,}}>
          <Spinner />
        </div>
        ) : (
          <></>
        )
      }
      <Routes>              
        <Route path="/timesheet" element={<Timesheet timesheetData={props.timesheetData} leaveData={props.leaveData} setLeaveData={props.setLeaveData} setTimesheetData={props.setTimesheetData} timesheetStatusMessage={props.timesheetStatusMessage} setTimesheetStatusMessage={props.setTimesheetStatusMessage} />} />
        <Route path="*" element={<Navigate to={"/timesheet"} />}></Route>
      </Routes>      
    </div> 
  </FluentProvider>
  );
}
